import type { TranslationKey } from '../i18n/translations/en';
import { Injectable, computed, inject } from '@angular/core';

import { AccessService } from '../access/access.service';
import { APP_NAVIGATION } from './navigation.config';
import type { NavItem, NavSection } from './navigation.models';
import { hasSubmenu, navDescendants, pathOf, routeMatches } from './navigation.utils';

/**
 * Projects the static navigation declaration through the current session's
 * roles, permissions and feature flags.
 *
 * Hiding an entry is a courtesy, not a security boundary: the route guard and,
 * ultimately, the backend are what actually deny access.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly access = inject(AccessService);

  /**
   * Sections with unreachable entries removed, and empty sections dropped.
   * An entry (or group) that declared children and has none left goes too —
   * that is how the Projects entry disappears when no project app is on.
   */
  readonly sections = computed<readonly NavSection[]>(() =>
    APP_NAVIGATION.map((section) => ({
      ...section,
      items: this.reachable(section.items),
    })).filter((section) => section.items.length > 0),
  );

  /** Flat list, groups included, useful for resolving a page title from the active URL. */
  readonly items = computed<readonly NavItem[]>(() =>
    this.sections().flatMap((section) =>
      section.items.flatMap((item) => [item, ...navDescendants(item)]),
    ),
  );

  /** The Projects entry as this session sees it, or null when it is hidden. */
  readonly projectsItem = computed<NavItem | null>(
    () =>
      this.sections()
        .flatMap((section) => section.items)
        .find((item) => item.projectScoped) ?? null,
  );

  /**
   * The primary item whose children belong in the second sidebar for this URL,
   * or null when the route has no submenu.
   */
  parentFor(url: string): NavItem | null {
    for (const section of this.sections()) {
      for (const item of section.items) {
        if (!hasSubmenu(item)) {
          continue;
        }

        if (
          routeMatches(url, item.route, item.exact) ||
          navDescendants(item).some((child) => routeMatches(url, child.route, child.exact))
        ) {
          return item;
        }
      }
    }

    return null;
  }

  /**
   * The translation key for the page a URL belongs to, not the text itself:
   * resolving it here would freeze the title in whatever language was active
   * when the route was matched. Longest-prefix match so `/users/42` still
   * resolves to the Users entry.
   */
  titleFor(url: string): TranslationKey | null {
    const path = pathOf(url);
    const topLevel = this.sections().flatMap((section) => section.items);

    return titleFrom(path, topLevel) ?? titleFrom(path, this.items());
  }

  private reachable(items: readonly NavItem[]): readonly NavItem[] {
    return items
      .filter((item) => this.access.satisfies(item.access))
      .flatMap((item) => {
        if (!item.children?.length) {
          return [item];
        }

        const children = this.reachable(item.children);

        return children.length > 0 ? [{ ...item, children }] : [];
      });
  }
}

function titleFrom(path: string, items: readonly NavItem[]): TranslationKey | null {
  // Template-aware, so `/projects/p1/tasker` resolves through `/projects/:projectId/tasker`.
  const matches = items.filter((item) => routeMatches(path, item.route));
  const exact = matches.find((item) => item.exact && path === item.route);

  return exact?.label ?? matches.sort((a, b) => b.route.length - a.route.length)[0]?.label ?? null;
}
